import { defineStore } from 'pinia';

/**
 * TTL configuration per collection (in milliseconds).
 * Collections with WebSocket subscriptions (orbit_alert, orbit_news)
 * use shorter TTLs since real-time updates keep them fresh.
 */
const TTL_CONFIG = {
  orbit_alert: 5 * 60 * 1000,                // 5 min (WebSocket-backed)
  orbit_links: 30 * 60 * 1000,               // 30 min
  orbit_news: 5 * 60 * 1000,                 // 5 min (WebSocket-backed)
  orbit_phonebook: 30 * 60 * 1000,           // 30 min
  orbit_floors: 60 * 60 * 1000,              // 1 hour
  meetingRooms: 30 * 60 * 1000,              // 30 min
  digital_forms: 30 * 60 * 1000,             // 30 min
  orgchart: 60 * 60 * 1000,                  // 1 hour
  orbit_documents: 30 * 60 * 1000,           // 30 min
  intranet_teams_forms: 30 * 60 * 1000,      // 30 min
  orbit_notification_channels: 60 * 60 * 1000, // 1 hour
  directus_users: 15 * 60 * 1000,            // 15 min
  orbit_spaces: 30 * 60 * 1000,              // 30 min (WebSocket-backed)
  // orbit_space_pages_<spaceId>: 15 min (default for this prefix handled by DEFAULT_TTL)
  // orbit_page_blocks_page_<pageId>: 5 min (view mode); forced fresh on edit
};

const DEFAULT_TTL = 30 * 60 * 1000; // 30 min fallback

// In-flight request map. Lives at module scope (not in Pinia state) because
// Promises are not serializable / persistable. Keyed by cache key — concurrent
// callers for the same key share a single network request.
const inflight = new Map();

export const useCollectionsStore = defineStore('collections', {
  state: () => ({
    // { [cacheKey]: { data: any, fetchedAt: number } }
    cache: {}
  }),
  persist: {
    paths: ['cache']
  },
  actions: {
    /**
     * Fetch data with stale-while-revalidate caching + in-flight dedup.
     *
     * @param {string} key - Cache key (usually collection name, can include suffix for variants)
     * @param {Function} fetchFn - Async function that returns the data to cache
     * @param {object} [options]
     * @param {number} [options.ttl] - Override TTL for this fetch
     * @param {boolean} [options.forceRefresh] - Skip cache, fetch fresh
     * @returns {Promise<{ data: any, fromCache: boolean }>}
     */
    async fetch(key, fetchFn, options = {}) {
      const ttl = options.ttl || TTL_CONFIG[key] || DEFAULT_TTL;
      const entry = this.cache[key];
      const now = Date.now();

      // Force refresh — share a single in-flight request across concurrent callers
      if (options.forceRefresh) {
        if (inflight.has(key)) {
          const data = await inflight.get(key);
          return { data, fromCache: false };
        }
        const promise = (async () => {
          try {
            const data = await fetchFn();
            this.cache[key] = { data, fetchedAt: Date.now() };
            return data;
          } finally {
            inflight.delete(key);
          }
        })();
        inflight.set(key, promise);
        const data = await promise;
        return { data, fromCache: false };
      }

      // Cache hit
      if (entry && entry.data != null) {
        const age = now - entry.fetchedAt;

        if (age < ttl) {
          // Fresh — return cached
          return { data: entry.data, fromCache: true };
        }

        // Stale — return cached immediately, refresh in background (deduped)
        if (!inflight.has(key)) {
          const promise = (async () => {
            try {
              const data = await fetchFn();
              this.cache[key] = { data, fetchedAt: Date.now() };
              return data;
            } finally {
              inflight.delete(key);
            }
          })();
          inflight.set(key, promise);
          promise.catch(() => {});
        }

        return { data: entry.data, fromCache: true };
      }

      // Cache miss — must fetch (deduped)
      if (inflight.has(key)) {
        const data = await inflight.get(key);
        return { data, fromCache: false };
      }
      const promise = (async () => {
        try {
          const data = await fetchFn();
          this.cache[key] = { data, fetchedAt: Date.now() };
          return data;
        } finally {
          inflight.delete(key);
        }
      })();
      inflight.set(key, promise);
      const data = await promise;
      return { data, fromCache: false };
    },

    /**
     * Update cached data directly (e.g., from WebSocket events).
     */
    updateCache(key, data) {
      this.cache[key] = { data, fetchedAt: Date.now() };
    },

    /**
     * Invalidate a single cache entry.
     */
    invalidate(key) {
      delete this.cache[key];
    },

    /**
     * Clear all collection caches (e.g., on logout).
     */
    clearAll() {
      this.cache = {};
    }
  }
});
